import * as XLSX from 'xlsx';
import { writeExcelFile } from '../../utils/excelExport';
import { getIndicatorName as getIndicatorDisplayName } from '../../utils/indicatorName';

const AggregationExportService = (countryBreakdowns, countriesState, indicatorsState, unitsState, regionMappingsData, categoriesState) => {

    const getCountryName = (countryCode) => {
        const country = countriesState.value[countryCode];
        return country ? country.c_short_name_eng : `Country ${countryCode}`;
    };

    const getIndicatorName = (indicatorCode) => {
        const indicator = indicatorsState.value[indicatorCode];
        return indicator ? getIndicatorDisplayName(indicator) : `Indicator ${indicatorCode}`;
    };

    const getCategoryName = (indicatorCode) => {
        const indicator = indicatorsState.value[indicatorCode];
        if (!indicator || !indicator.cat_code) return '';
        const category = categoriesState.value[indicator.cat_code];
        return category ? category.cat_name_eng : '';
    };

    const getUnitName = (unitCode) => {
        const unit = unitsState.value[unitCode];
        return unit ? unit.unit_name_eng : `Unit ${unitCode}`;
    };

    const getRegionName = (regionCode) => {
        const region = regionMappingsData.find(region => region.id === regionCode);
        return region ? region.region_name : `Region ${regionCode}`;
    };

    const exportDetailsToExcel = () => {
        if (!Array.isArray(countryBreakdowns) || countryBreakdowns.length === 0) {
            console.error('No data to export');
            return;
        }

        const workbook = XLSX.utils.book_new();

        const groupedData = {};
        
        countryBreakdowns.forEach(item => {
            const key = `${item.year}-${item.ind_code}`;
            if (!groupedData[key]) {
                groupedData[key] = {
                    year: item.year,
                    ind_code: item.ind_code,
                    r_code: item.r_code,
                    unit_code: item.unit_code,
                    items: []
                };
            }
            groupedData[key].items.push(item);
        });

        Object.entries(groupedData).forEach(([key, group]) => {
            const indicatorName = getIndicatorName(group.ind_code);
            const year = group.year;
            const baseSheetName = `${year}-${group.ind_code}`;

            const countriesData = new Map();
            
            group.items.forEach(item => {
                if (item.base_indicator_breakdown) {
                    Object.entries(item.base_indicator_breakdown).forEach(([countryCode, value]) => {
                        if (!countriesData.has(countryCode)) {
                            countriesData.set(countryCode, {
                                countryCode: parseInt(countryCode),
                                baseValue: null,
                                baseDate: null,
                                weightedValue: null,
                                weightedDate: null,
                                weightValue: null,
                                resultValue: null,
                                baseIndCode: null,
                                weightedIndCode: null,
                                baseUnitCode: null,
                                weightedUnitCode: null
                            });
                        }
                        const data = countriesData.get(countryCode);
                        data.baseValue = value;
                        data.baseIndCode = item.ind_code;
                        data.baseUnitCode = item.unit_code;
                    });
                }

                if (item.weighted_indicator_breakdown) {
                    Object.entries(item.weighted_indicator_breakdown).forEach(([countryCode, value]) => {
                        if (!countriesData.has(countryCode)) {
                            countriesData.set(countryCode, {
                                countryCode: parseInt(countryCode),
                                baseValue: null,
                                baseDate: null,
                                weightedValue: null,
                                weightedDate: null,
                                weightValue: null,
                                resultValue: null,
                                baseIndCode: null,
                                weightedIndCode: null,
                                baseUnitCode: null,
                                weightedUnitCode: null
                            });
                        }
                        const data = countriesData.get(countryCode);
                        data.weightedValue = value;
                        data.weightedIndCode = item.weighted_ind_code;
                        data.weightedUnitCode = item.weighted_unit_code;
                    });
                }

                if (item.country_breakdown) {
                    Object.entries(item.country_breakdown).forEach(([countryCode, value]) => {
                        if (!countriesData.has(countryCode)) {
                            countriesData.set(countryCode, {
                                countryCode: parseInt(countryCode),
                                baseValue: null,
                                baseDate: null,
                                weightedValue: null,
                                weightedDate: null,
                                weightValue: null,
                                resultValue: null,
                                baseIndCode: null,
                                weightedIndCode: null,
                                baseUnitCode: null,
                                weightedUnitCode: null
                            });
                        }
                        const data = countriesData.get(countryCode);
                        data.resultValue = value;
                    });
                }

                if (item.country_weights_breakdown) {
                    Object.entries(item.country_weights_breakdown).forEach(([countryCode, value]) => {
                        if (!countriesData.has(countryCode)) {
                            countriesData.set(countryCode, {
                                countryCode: parseInt(countryCode),
                                baseValue: null,
                                baseDate: null,
                                weightedValue: null,
                                weightedDate: null,
                                weightValue: null,
                                resultValue: null,
                                baseIndCode: null,
                                weightedIndCode: null,
                                baseUnitCode: null,
                                weightedUnitCode: null
                            });
                        }
                        const data = countriesData.get(countryCode);
                        data.weightValue = value;
                    });
                }
            });

            const sortedCountries = Array.from(countriesData.values()).sort((a, b) => 
                getCountryName(a.countryCode).localeCompare(getCountryName(b.countryCode))
            );

            if (sortedCountries.length > 0 && sortedCountries[0].baseIndCode) {
                const baseIndicatorName = getIndicatorName(sortedCountries[0].baseIndCode);
                const baseCategoryName = getCategoryName(sortedCountries[0].baseIndCode);
                const baseUnitName = getUnitName(sortedCountries[0].baseUnitCode);
                
                const weightedIndicatorName = sortedCountries[0].weightedIndCode ? 
                    getIndicatorName(sortedCountries[0].weightedIndCode) : '';
                const weightedCategoryName = sortedCountries[0].weightedIndCode ?
                    getCategoryName(sortedCountries[0].weightedIndCode) : '';
                const weightedUnitName = sortedCountries[0].weightedUnitCode ?
                    getUnitName(sortedCountries[0].weightedUnitCode) : '';

                const detailedData = [];
                
                detailedData.push([
                    'BASE INDICATOR', '', '', '', '', '', '', '',
                    'WEIGHTED INDICATOR', '', '', '', '', '', '', '',
                    'WEIGHTS AND RESULT', ''
                ]);
                
                detailedData.push([
                    'Country Name', 'Category', 'Indicator Code', 'Indicator', 'Unit', 'Year', 'Date', 'Value',
                    'Country Name', 'Category', 'Indicator Code', 'Indicator', 'Unit', 'Year', 'Date', 'Value',
                    'Country Name', 'Weight'
                ]);

                sortedCountries.forEach(countryData => {
                    const countryName = getCountryName(countryData.countryCode);
                    
                    const weightValue = countryData.weightValue !== null && countryData.weightValue !== undefined ? 
                        countryData.weightValue : '';
                    
                    detailedData.push([
                        countryName,
                        baseCategoryName,
                        countryData.baseIndCode || '',
                        baseIndicatorName,
                        baseUnitName,
                        year,
                        countryData.baseDate || '',
                        countryData.baseValue !== null && countryData.baseValue !== undefined ? countryData.baseValue : '',
                        
                        countryName,
                        weightedCategoryName,
                        countryData.weightedIndCode || '',
                        weightedIndicatorName,
                        weightedUnitName,
                        year,
                        countryData.weightedDate || '',
                        countryData.weightedValue !== null && countryData.weightedValue !== undefined ? countryData.weightedValue : '',
                        
                        countryName,
                        weightValue
                    ]);
                });

                const sumWeightedValue = sortedCountries.reduce((sum, countryData) => {
                    const value = countryData.weightedValue;
                    return sum + (value !== null && value !== undefined && !isNaN(value) ? parseFloat(value) : 0);
                }, 0);

                const totalWeight = sortedCountries.reduce((sum, countryData) => {
                    const weight = countryData.weightValue;
                    return sum + (weight !== null && weight !== undefined && !isNaN(weight) ? parseFloat(weight) : 0);
                }, 0);

                const aggregatedValue = group.items[0]?.aggregated_value || totalWeight;

                detailedData.push([
                    '', '', '', '', '', '', '', '',
                    '', '', '', '', '', '', 'SUM', sumWeightedValue,
                    'WEIGHT', aggregatedValue
                ]);

                const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
                
                const summaryRowIndex = detailedData.length - 1;
                const sumCellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: 14 });
                const sumValueCellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: 15 });
                const weightCellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: 16 });
                const weightValueCellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: 17 });
                
                if (!detailedSheet[sumCellAddress]) detailedSheet[sumCellAddress] = { v: 'SUM', t: 's' };
                if (!detailedSheet[weightCellAddress]) detailedSheet[weightCellAddress] = { v: 'WEIGHT', t: 's' };
                
                detailedSheet[sumCellAddress].s = { fill: { fgColor: { rgb: 'FFFF00' } }, font: { bold: true } };
                detailedSheet[sumValueCellAddress].s = { fill: { fgColor: { rgb: 'FFFF00' } } };
                detailedSheet[weightCellAddress].s = { fill: { fgColor: { rgb: 'FFFF00' } }, font: { bold: true } };
                detailedSheet[weightValueCellAddress].s = { fill: { fgColor: { rgb: 'FFFF00' } } };
                
                detailedSheet['!cols'] = [
                    { width: 20 }, { width: 30 }, { width: 15 }, { width: 40 }, { width: 25 }, { width: 10 }, { width: 12 }, { width: 12 },
                    { width: 20 }, { width: 30 }, { width: 15 }, { width: 40 }, { width: 25 }, { width: 10 }, { width: 12 }, { width: 12 },
                    { width: 20 }, { width: 20 }
                ];
                
                XLSX.utils.book_append_sheet(workbook, detailedSheet, `${baseSheetName}-Detail`);
            }

            const resultsData = [];
            const firstItem = group.items[0];
            const baseIndicatorName = getIndicatorName(firstItem.ind_code);
            const weightedIndicatorName = firstItem.weighted_ind_code ? 
                getIndicatorName(firstItem.weighted_ind_code) : '';

            resultsData.push([
                'Country Name',
                `${year} Result`,
                `${year} ${baseIndicatorName}`,
                `${year} ${baseIndicatorName} (Weighted)`
            ]);

            sortedCountries.forEach(countryData => {
                resultsData.push([
                    getCountryName(countryData.countryCode),
                    countryData.resultValue !== null && countryData.resultValue !== undefined ? countryData.resultValue : '',
                    countryData.baseValue !== null && countryData.baseValue !== undefined ? countryData.baseValue : '',
                    countryData.weightedValue !== null && countryData.weightedValue !== undefined ? countryData.weightedValue : ''
                ]);
            });

            const resultsSheet = XLSX.utils.aoa_to_sheet(resultsData);
            resultsSheet['!cols'] = [
                { width: 25 },
                { width: 20 },
                { width: 40 },
                { width: 40 }
            ];
            
            XLSX.utils.book_append_sheet(workbook, resultsSheet, `${baseSheetName}-Results`);

            const summaryData = [];
            summaryData.push(['AGGREGATION SUMMARY', '', '', '']);
            summaryData.push(['Region', 'Indicator', 'Year', 'Aggregated Value']);
            
            group.items.forEach(item => {
                summaryData.push([
                    getRegionName(item.r_code),
                    getIndicatorName(item.ind_code),
                    item.year,
                    item.aggregated_value || ''
                ]);
            });

            const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
            summarySheet['!cols'] = [
                { width: 25 },
                { width: 50 },
                { width: 10 },
                { width: 20 }
            ];
            
            XLSX.utils.book_append_sheet(workbook, summarySheet, `${baseSheetName}-Summary`);
        });

        const filename = `aggregation-breakdown-${new Date().toISOString().split('T')[0]}.xlsx`;
        writeExcelFile(workbook, filename);
    };

    return { exportDetailsToExcel };
};

export default AggregationExportService;